import { formatPlanTokens, formatTry, splitVat } from './plans.js';

/**
 * Phase 16c.1: the Ön Bilgilendirme Formu's and the Mesafeli Satış Sözleşmesi's fields that come from the package, not
 * from fixed text: `{haklar}` (the rights, one per line), `{haklar kısaca}` (the same in one sentence) and
 * `{fiyat tablosu}` (monthly and yearly, VAT included and apart). At a purchase they are the order's (what the person
 * buys); on the public pages, Pro's as it is sold now. The texts are Turkish in both languages.
 */
export interface LegalPlanFields {
  monthlyPriceKurus: number;
  yearlyPriceKurus: number | null;
  dailyTokens: number;
  maxDocuments: number;
  maxFileMb: number;
  /** null: no limit, 0: none */
  maxOwnSkills: number | null;
  maxOwnConnections: number | null;
  /** the chat models' names, the package's order */
  chatModels: string[];
}

const count = (n: number) => n.toLocaleString('tr-TR');

/** The rights, each as the forms list them */
export function legalRights(plan: LegalPlanFields): string[] {
  const own = (limit: number | null, one: string) => (limit === null ? `Sınırsız kendi ${one}` : limit === 0 ? null : `${count(limit)} kendi ${one}`);
  return [
    `Günde ${formatPlanTokens(plan.dailyTokens, 'tr')} token`,
    `${count(plan.maxDocuments)} belge, dosya başına en çok ${count(plan.maxFileMb)} MB`,
    plan.chatModels.length > 0 ? `${count(plan.chatModels.length)} sohbet modeli: ${plan.chatModels.join(', ')}` : null,
    own(plan.maxOwnSkills, 'skill’i'),
    own(plan.maxOwnConnections, 'bağlantısı (MCP)'),
    'Ücretsiz paketteki bütün özellikler: sohbet, web araması, skill’ler, dosya hazırlama, hafıza',
  ].filter((line): line is string => line !== null);
}

export function legalPriceTable(plan: Pick<LegalPlanFields, 'monthlyPriceKurus' | 'yearlyPriceKurus'>): string {
  const row = (label: string, kurus: number) => {
    const { net, vat } = splitVat(kurus);
    return `| ${label} | ${formatTry(kurus)} | ${formatTry(net)} | ${formatTry(vat)} |`;
  };
  return [
    '| Seçenek | Toplam (KDV dahil) | KDV hariç | KDV (%20) |',
    '| --- | --- | --- | --- |',
    row('Aylık', plan.monthlyPriceKurus),
    ...(plan.yearlyPriceKurus !== null ? [row('Yıllık', plan.yearlyPriceKurus)] : []),
  ].join('\n');
}

/** The package's fields filled; the order's own (`{ad soyad}`…) are left for the purchase */
export function fillPlanFields(body: string, plan: LegalPlanFields): string {
  const rights = legalRights(plan);
  const sentence = rights.map((line, i) => (i === 0 ? line : line.charAt(0).toLocaleLowerCase('tr') + line.slice(1))).join(', ');
  const fields: [string, string][] = [
    ['{haklar}', rights.map((line) => `- ${line}`).join('\n')],
    ['{haklar kısaca}', sentence],
    ['{fiyat tablosu}', legalPriceTable(plan)],
  ];
  let text = body;
  for (const [field, value] of fields) text = text.split(`\`${field}\``).join(value).split(field).join(value);
  return text;
}
